import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FiCheck, FiX, FiStar } from 'react-icons/fi';
import { getSubscriptionPlans } from '../../services/api';
import { fmtCurrency } from '../../utils/formatNumber';
import styles from './LandingLayout.module.css';

const COMMON_FEATURES = [
  'Full donation & offering management',
  'Family & member management',
  'Double-entry accounting ledger',
  'Financial year & opening balance',
  'Reports: balance sheet, I&E, day book',
  'Multi-Derasar support',
  'Role-based access control',
  'Online payment via Razorpay',
  'PDF receipts & email delivery',
];

const MEMBER_LABEL = { basic: 'Up to 50 members', standard: 'Up to 100 members', premium: 'Unlimited members' };

const TIER_FEATURES = {
  basic: [
    { label: MEMBER_LABEL.basic, included: true },
    { label: 'Email alerts', included: false },
    { label: 'WhatsApp alerts', included: false },
  ],
  standard: [
    { label: MEMBER_LABEL.standard, included: true },
    { label: 'Email alerts', included: true },
    { label: 'WhatsApp alerts', included: false },
  ],
  premium: [
    { label: MEMBER_LABEL.premium, included: true },
    { label: 'Email alerts', included: true },
    { label: 'WhatsApp alerts', included: true },
  ],
};

const getFeatures = (tier) => [
  ...(TIER_FEATURES[tier] || TIER_FEATURES.basic),
  ...COMMON_FEATURES.map((label) => ({ label, included: true })),
];

const PricingPage = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getSubscriptionPlans()
      .then(res => {
        const all = res?.data?.data?.plans ?? res?.data?.plans ?? [];
        setPlans(all.filter(p => !p.isSpecial));
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  return (
    <section className={styles.section}>
      <div className={styles.sectionInner}>
        <div className={styles.sectionLabel}>Pricing</div>
        <h2 className={styles.sectionTitle}>Simple, Transparent Pricing</h2>
        <p className={styles.sectionLead}>
          Choose the plan that fits your Derasar's size and needs. No hidden fees —
          upgrade anytime as your community grows.
        </p>

        {loading ? (
          <p style={{ color: 'var(--color-text-secondary)' }}>Loading plans…</p>
        ) : (
          <div className={styles.pricingGrid}>
            {plans.map((plan) => {
              const featured = plan.tier === 'premium';
              const features = getFeatures(plan.tier);
              return (
                <div
                  key={plan.id}
                  className={`${styles.pricingCard} ${featured ? styles.pricingCardFeatured : ''}`}
                >
                  {featured && (
                    <div className={styles.pricingBadge}>
                      <FiStar size={11} /> Most Popular
                    </div>
                  )}
                  <div className={styles.pricingLabel}>{plan.label}</div>
                  <div className={styles.pricingPrice}>
                    <span className={`${styles.pricingAmount} ${featured ? styles.pricingAmountFeatured : ''}`}>
                      {fmtCurrency(plan.price)}
                    </span>
                    <span className={styles.pricingPer}>
                      / {plan.years} yr{plan.years > 1 ? 's' : ''}
                    </span>
                  </div>
                  {plan.years > 1 && (
                    <div className={styles.pricingPerYear}>
                      {fmtCurrency(Math.round(plan.price / plan.years))} per year
                    </div>
                  )}
                  <ul className={styles.pricingFeatures}>
                    {features.map((f) => (
                      <li key={f.label} className={f.included ? '' : styles.pricingFeatureExcluded}>
                        {f.included
                          ? <FiCheck className={styles.pricingCheck} />
                          : <FiX className={styles.pricingCross} />}
                        {f.label}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/login/admin?tab=register"
                    className={`${styles.pricingCta} ${featured ? styles.pricingCtaPrimary : styles.pricingCtaSecondary}`}
                  >
                    Get Started
                  </Link>
                </div>
              );
            })}
          </div>
        )}

        <div className={styles.pricingRenewalCard}>
          <FiStar size={16} />
          On renewal, we offer a <strong>25% discount</strong> on the listed price.
        </div>
      </div>
    </section>
  );
};

export default PricingPage;
